import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Grid } from '@mui/material';

import { WidgetProps, useModelState } from '@utils/widget-model';
import { theme } from '@utils/widget-theme';

import { FeatureExplorationPaths } from './FeatureExplorationPaths';
import { FeatureSpaceWidgetState } from '../FeatureSpaceWidget';
import {
  DomainsDescriptionsTreeItemSelectionContextProvider,
  DescriptionsTree,
  DomainsDescriptionsTreeConfigContextProvider,
  DomainsDescriptionsSelectionContextProvider,
  DomainsDescriptionsTreeConfig,
} from '../../feature-descriptor/domains-descriptions-tree';
import { FeatureDomainsDescriptions } from './FeatureDomainsDescriptions';

const Divider = () => (
  <div
    style={{
      height: '1px',
      borderTop: '1px solid var(--c-grey-300)',
      marginBlock: '24px',
    }}
  ></div>
);

export const FeatureSpaceView: React.FC<WidgetProps> = props => {
  const [featureSpace] = useModelState<FeatureSpaceWidgetState, 'value'>(
    'value'
  );

  const [configuration] = useModelState<
    FeatureSpaceWidgetState,
    'configuration'
  >('configuration');

  const selectedJoinsModel = useModelState<
    FeatureSpaceWidgetState,
    '_selected_joins'
  >('_selected_joins');

  const selectedFiltersModel = useModelState<
    FeatureSpaceWidgetState,
    '_selected_filters'
  >('_selected_filters');

  const selectedAggregationsModel = useModelState<
    FeatureSpaceWidgetState,
    '_selected_aggregations'
  >('_selected_aggregations');

  const [filterDescriptionsTree, setFilterDescriptionsTree] =
    React.useState<DescriptionsTree>(() => ({ types: [] }));
  const [aggregationDescriptionsTree, setAggregationDescriptionsTree] =
    React.useState<DescriptionsTree>(() => ({ types: [] }));

  React.useEffect(() => {
    setFilterDescriptionsTree(
      DescriptionsTree.appendNodeId(featureSpace.filters, 'f')
    );
  }, [featureSpace.filters]);
  React.useEffect(() => {
    setAggregationDescriptionsTree(
      DescriptionsTree.appendNodeId(featureSpace.aggregations, 'a')
    );
  }, [featureSpace.aggregations]);

  const descriptionTreeConfig: DomainsDescriptionsTreeConfig = React.useMemo(
    () => ({
      isWithCheckboxes: configuration.selectable,
      areCheckboxesDisabled: configuration.disabled,
    }),
    [configuration]
  );

  return (
    <ThemeProvider theme={theme}>
      <DomainsDescriptionsTreeConfigContextProvider
        configuration={descriptionTreeConfig}
      >
        <DomainsDescriptionsTreeItemSelectionContextProvider>
          <FeatureExplorationPaths
            explorationPaths={featureSpace.explorationPaths}
            selectedJoinIds={selectedJoinsModel[0]}
            setSelectedJoinIds={selectedJoinsModel[1]}
          />
          <Divider />
          <Grid container spacing={6}>
            <Grid item xs={6}>
              <DomainsDescriptionsSelectionContextProvider
                selectedDomainDescriptionIds={selectedFiltersModel[0]}
                setSelectedDomainDescriptionIds={selectedFiltersModel[1]}
              >
                <FeatureDomainsDescriptions
                  domainName="Filters"
                  descriptionsTree={filterDescriptionsTree}
                  selectedJoinIds={selectedJoinsModel[0]}
                />
              </DomainsDescriptionsSelectionContextProvider>
            </Grid>
            <Grid item xs={6} sx={{ overflow: 'hidden' }}>
              <DomainsDescriptionsSelectionContextProvider
                selectedDomainDescriptionIds={selectedAggregationsModel[0]}
                setSelectedDomainDescriptionIds={selectedAggregationsModel[1]}
              >
                <FeatureDomainsDescriptions
                  domainName="Aggregations"
                  descriptionsTree={aggregationDescriptionsTree}
                  selectedJoinIds={selectedJoinsModel[0]}
                />
              </DomainsDescriptionsSelectionContextProvider>
            </Grid>
          </Grid>
        </DomainsDescriptionsTreeItemSelectionContextProvider>
      </DomainsDescriptionsTreeConfigContextProvider>
    </ThemeProvider>
  );
};
